import React, { useEffect, useState } from 'react';

import type { VideoComponent } from '../../../../../core/data-sources/content-components/types.js';

import { isObject } from '../../../../../core/utils/type-guards.js';

interface Dimension {
  width: number | string;
  height: number | string;
}

const normalizeVideo = (
  video: VideoComponent,
  isMobile: boolean,
  isInListicle: boolean,
): Dimension => {
  const rawAspectRatio = video.videoAsset.metadata?.aspectRatio;
  const aspectRatio = rawAspectRatio && parseFloat(rawAspectRatio);
  if (isMobile && aspectRatio) {
    if (isInListicle) {
      return {
        width: 'calc(100vw - 32px)',
        height: `calc((100vw - 32px) / ${aspectRatio})`,
      };
    }

    return {
      width: '100vw',
      height: `${100 / aspectRatio}vw`,
    };
  }
  const standardWidth =
    video?.presentationProperties?.addLayout === 'narrow' ? 490 : 784;
  const source = video.videoAsset.sourceFiles?.[0];

  if (!source && aspectRatio) {
    return {
      width: standardWidth,
      height: standardWidth / aspectRatio,
    };
  }

  if (source?.height && source?.width) {
    return {
      width: standardWidth,
      height: (standardWidth * source.height) / source.width,
    };
  }

  const fallbackAspectRatio = 1.78;

  return {
    width: standardWidth,
    height: standardWidth / fallbackAspectRatio,
  };
};

interface VideoAssetPlayerProps {
  video: VideoComponent;
  isMobile: boolean;
  isInListicle: boolean;
}

const VideoAssetPlayer: React.FC<VideoAssetPlayerProps> = ({
  video,
  isMobile,
  isInListicle,
}) => {
  const id = parseInt(video.videoAsset.id);
  const [dimensions, setDimensions] = useState<Dimension | null>(null);

  // calculate dimensions as soon as video and isMobile props are available
  useEffect(() => {
    const newDimensions = normalizeVideo(video, isMobile, isInListicle);
    setDimensions(newDimensions);
  }, [video, isMobile, isInListicle]);

  useEffect(() => {
    const onReadyHandler = () => {
      if (!dimensions) {
        return;
      }

      new SVP.Player({
        id,
        vendor: video.provider.id,
        node: `video-${id}`,
        ...dimensions,
        recommended: false,
        pulse: {
          provider: 'aftenposten',
          decorator: (eventData: Record<string, unknown>) => {
            return {
              ...(isObject(eventData) ? eventData : {}),
              provider: {
                ...(isObject(eventData) && isObject(eventData.provider)
                  ? eventData.provider
                  : {}),
                productTag: 'vink',
                productType: 'ResponsiveWeb',
                product: 'fastenposten',
              },
            };
          },
        },
        repeat: video?.presentationProperties?.intention === 'looping',
        autoplay:
          video?.presentationProperties?.intention === 'looping'
            ? 'viewable'
            : false,
        autopause: 'viewable',
        mute: true,
      });
    };

    window.addEventListener('onSvpPlayerReady', onReadyHandler);

    return () => {
      window.removeEventListener('onSvpPlayerReady', onReadyHandler);
    };
  }, [id, video, dimensions]);

  return (
    <div className="svp-video-element">
      <div id={`video-${id}`}></div>
    </div>
  );
};

export default VideoAssetPlayer;
